import { forwardRef, type ButtonHTMLAttributes, type ReactElement } from 'react';
import { Icon, type IconName } from './icon';
import { cn } from '@/lib/cn';

export type IconButtonVariant = 'ghost' | 'soft' | 'surface' | 'filled';

const VARIANT_CLASSES: Readonly<Record<IconButtonVariant, string>> = {
  ghost: 'ghost text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--bg-soft)]',
  soft: 'soft bg-[var(--bg-soft)] text-[var(--ink)] hover:bg-[var(--bg-inset)]',
  surface:
    'surface bg-[var(--bg-surface)] border border-[var(--rule)] text-[var(--ink-2)] hover:text-[var(--ink)]',
  filled: 'filled bg-[var(--ink)] text-[var(--bg-surface)] hover:bg-[var(--brand-ink)]',
};

export type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  name: IconName;
  size?: number;
  iconSize?: number;
  variant?: IconButtonVariant;
  active?: boolean;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { name, size = 36, iconSize, variant = 'ghost', active = false, className, type = 'button', style, ...rest },
  ref,
): ReactElement {
  const resolvedVariant: IconButtonVariant = active ? 'filled' : variant;
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center rounded-[var(--r-sm)] transition-colors',
        VARIANT_CLASSES[resolvedVariant],
        className,
      )}
      style={{ width: size, height: size, ...style }}
      {...rest}
    >
      <Icon name={name} size={iconSize ?? Math.round(size * 0.45)} />
    </button>
  );
});
